import { Typography } from '@mui/material'
import { append, remove, update } from 'ramda'
import React from 'react'

import { canVoiceTranscribe } from '@/app/module/campaigns/ai-helpers'
import { CampaignAddButton } from '@/app/module/campaigns/component/helpers'
import { useCampaignActionContext } from '@/app/module/campaigns/component/item-provider'
import { CampaignTemplateType, LocalOnAnswerType, PartFamily } from '@/app/module/campaigns/types'
import { getActionOptionsForCampaign } from '@/app/module/campaigns/utils/actions/options'
import { ActionContext } from '@/app/module/campaigns/utils/actions/types'

import { ActionProps, ApiProps } from './part-config-props'
import ResponseWhatsappSpecificItem from './response-whatsapp-specific-item'
import { emptyRanges, emptyReplies } from './types/multimessage/definitions'
import UseAiToggle from './use-ai-toggle'
import WhatsAppTranscribeToggle from './whatsapp-transcribe-toggle'

type Props = {
  actionProps: ActionProps
  apiProps: ApiProps
  campaignType: CampaignTemplateType
  id: string
  onAnswer: LocalOnAnswerType[]
  partType: PartFamily
  transcribe: boolean
  useAi: boolean

  onChange: (value: { onAnswer?: LocalOnAnswerType[]; transcribe?: boolean; useAi?: boolean }) => void
}

const ResponseWhatsAppSpecific: React.FC<Props> = ({
  actionProps,
  apiProps,
  campaignType,
  id,
  onAnswer,
  partType,
  transcribe,
  useAi,
  onChange,
}) => {
  const { hasQuotas } = useCampaignActionContext()
  const answerActions = React.useMemo(
    () =>
      getActionOptionsForCampaign({
        campaignType,
        partType,
        hasQuotas,
        actionNamespace: ActionContext.OnAnswer,
      }),
    [campaignType, hasQuotas, partType],
  )

  const handleChange = React.useCallback(
    (value: LocalOnAnswerType, choiceIndex: number) => {
      const currentChoice = onAnswer[choiceIndex]
      onChange({
        onAnswer: update(
          choiceIndex,
          {
            ...currentChoice,
            actions: value.actions || currentChoice.actions || [],
            label: typeof value.label === 'string' ? value.label : currentChoice.label || '',
            ranges: value.ranges || currentChoice.ranges || [],
            replies: value.replies || currentChoice.replies || [],
          },
          onAnswer,
        ),
      })
    },
    [onAnswer, onChange],
  )

  const handleRemove = React.useCallback(
    (choiceIndex: number) => onChange({ onAnswer: remove(choiceIndex, 1, onAnswer) }),
    [onAnswer, onChange],
  )

  return (
    <div className="campaign-specific-tab-content">
      <Typography>Choices</Typography>
      <div>
        <Typography color="textSecondary" variant="caption" gutterBottom={true}>
          Specify the valid answer choices to this question and what actions we should take for each choice.
          <Typography color="primary" variant="caption" sx={{ fontWeight: 'bold' }}>
            <br />
            Pro Tip: Use WhatsApp List or WhatsApp Buttons instead of Specific Response to increase response rates.
          </Typography>
        </Typography>
        {canVoiceTranscribe(partType) && (
          <WhatsAppTranscribeToggle
            context={ActionContext.OnAnswer}
            transcribe={transcribe}
            onChange={(v) => onChange({ transcribe: v })}
          />
        )}
        <UseAiToggle useAi={useAi} onChange={(v) => onChange({ useAi: v })} />
        {onAnswer.map(({ ranges = emptyRanges, replies = emptyReplies, actions, label = '' }, i: number) => (
          <ResponseWhatsappSpecificItem
            key={`whatsapp-${i}`}
            actionOptions={answerActions}
            actionProps={actionProps}
            actions={actions}
            apiProps={apiProps}
            choiceIndex={i}
            id={id}
            isCloseDisabled={onAnswer.length === 1}
            label={label}
            ranges={ranges}
            replies={replies}
            transcribeEnabled={transcribe}
            onChange={handleChange}
            onRemove={handleRemove}
          />
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <CampaignAddButton
            tooltip="Add choice"
            onClick={() => {
              onChange({
                onAnswer: append(
                  {
                    replies: [],
                    reply: '',
                    ranges: [],
                    actions: [],
                    label: '',
                  },
                  onAnswer,
                ),
              })
            }}
            text="Add choice"
            icon="add"
          />
        </div>
      </div>
    </div>
  )
}

export default React.memo(ResponseWhatsAppSpecific)
